import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Upload, Phone, User, MapPin, ArrowRight, Ruler, Copy, Check, ShieldCheck, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ordersApi } from '@/services/api';
import { toast } from 'sonner';
import OrderLayout from './OrderLayout';

const InputField = ({ label, icon: Icon, required, children }) => (
  <div>
    <label className="flex items-center gap-1.5 text-sm font-medium text-charcoal/70 mb-2">
      {Icon && <Icon size={13} />}
      {label}
      {required && <span className="text-gold">*</span>}
    </label>
    {children}
  </div>
);

const inputCls =
  'w-full px-4 py-3 border border-charcoal/15 bg-white/70 text-charcoal placeholder:text-charcoal/35 focus:outline-none focus:border-gold transition-colors';

const CredentialBox = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();

  const handleCopy = () => {
    navigator.clipboard.writeText(value).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white border border-charcoal/12 rounded-sm p-4">
      <p className="text-xs font-semibold text-charcoal/50 uppercase tracking-widest mb-2">{label}</p>
      <div className="flex items-center justify-between gap-3">
        <p className="text-2xl font-bold text-charcoal tracking-widest font-mono" dir="ltr">{value}</p>
        <button
          type="button"
          onClick={handleCopy}
          className="flex items-center gap-1.5 text-xs font-semibold text-charcoal/50 hover:text-gold transition-colors px-3 py-1.5 border border-charcoal/15 hover:border-gold/30"
        >
          {copied ? <Check size={13} className="text-green-500" /> : <Copy size={13} />}
          {copied ? t('order.credentials.copied') : t('order.credentials.copy')}
        </button>
      </div>
    </div>
  );
};

const StartPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [planFile, setPlanFile] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [form, setForm] = useState({
    customer_name: '',
    customer_phone: '',
    customer_address: '',
    length: '',
    width: '',
    height: '',
    notes: '',
  });

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim() || !form.customer_phone.trim() || !form.customer_address.trim()) {
      toast.error(t('order.error'));
      return;
    }

    setLoading(true);
    try {
      const payload = {
        customer_name: form.customer_name,
        customer_phone: form.customer_phone,
        customer_address: form.customer_address,
        measurements: {
          length: form.length ? parseFloat(form.length) : null,
          width: form.width ? parseFloat(form.width) : null,
          height: form.height ? parseFloat(form.height) : null,
          notes: form.notes || null,
          plan_file: null,
        },
      };

      const res = await ordersApi.create(payload);
      const order = res.data;

      localStorage.setItem(
        'arman_order',
        JSON.stringify({
          orderId: order.id,
          orderNumber: order.order_number,
          customerName: order.customer_name,
          customerPhone: order.customer_phone,
          accessPassword: order.access_password,
        })
      );

      if (planFile) {
        setUploadLoading(true);
        try {
          await ordersApi.uploadPlan(order.id, planFile);
          toast.success(t('order.upload.success'));
        } catch {
          toast.warning(t('order.upload.failed'));
        } finally {
          setUploadLoading(false);
        }
      }

      setCredentials({
        orderNumber: order.order_number,
        password: order.access_password,
      });
    } catch {
      toast.error(t('order.error'));
    } finally {
      setLoading(false);
    }
  };

  if (credentials) {
    return (
      <OrderLayout currentStep={1} orderNumber={credentials.orderNumber}>
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="space-y-6 text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', delay: 0.1 }}
              className="w-20 h-20 bg-gold/10 rounded-full flex items-center justify-center mx-auto"
            >
              <ShieldCheck className="text-gold" size={40} />
            </motion.div>

            <div>
              <h2 className="text-2xl font-bold text-charcoal mb-2">{t('order.credentials.title')}</h2>
              <p className="text-charcoal/60 text-sm">{t('order.credentials.desc')}</p>
            </div>

            <div className="space-y-3 text-start">
              <CredentialBox label={t('order.credentials.orderNum')} value={credentials.orderNumber} />
              <CredentialBox label={t('order.credentials.password')} value={credentials.password} />
            </div>

            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 p-4 text-start">
              <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-amber-700 text-sm font-medium">{t('order.credentials.warning')}</p>
            </div>

            <motion.button
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/schedule-visit')}
              className="w-full bg-gold text-charcoal-deep font-bold py-4 flex items-center justify-center gap-3 hover:opacity-90 transition-opacity text-base"
            >
              {t('order.credentials.continue')} <ArrowRight size={20} />
            </motion.button>
          </motion.div>
        </AnimatePresence>
      </OrderLayout>
    );
  }

  return (
    <OrderLayout currentStep={1}>
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Customer Info */}
        <div className="grid md:grid-cols-3 gap-5">
          <InputField label={t('order.form.name')} icon={User} required>
            <input
              type="text"
              name="customer_name"
              value={form.customer_name}
              onChange={handleChange}
              required
              className={inputCls}
              placeholder={t('order.form.name')}
            />
          </InputField>
          <InputField label={t('order.form.phone')} icon={Phone} required>
            <input
              type="tel"
              name="customer_phone"
              value={form.customer_phone}
              onChange={handleChange}
              required
              className={inputCls}
              placeholder="+966 5X XXX XXXX"
              dir="ltr"
            />
          </InputField>
          <InputField label={t('order.form.address')} icon={MapPin} required>
            <input
              type="text"
              name="customer_address"
              value={form.customer_address}
              onChange={handleChange}
              required
              className={inputCls}
              placeholder={t('order.form.address')}
            />
          </InputField>
        </div>

        {/* Measurements */}
        <div className="border-t border-charcoal/8 pt-7">
          <p className="label-text mb-5">
            <Ruler size={12} className="inline me-2" />
            {t('order.steps.measurements.title')}
          </p>
          <div className="grid md:grid-cols-3 gap-5">
            <InputField label={t('order.form.length')}>
              <input
                type="number"
                name="length"
                value={form.length}
                onChange={handleChange}
                min="0"
                step="0.01"
                className={inputCls}
                placeholder="350"
                dir="ltr"
              />
            </InputField>
            <InputField label={t('order.form.width')}>
              <input
                type="number"
                name="width"
                value={form.width}
                onChange={handleChange}
                min="0"
                step="0.01"
                className={inputCls}
                placeholder="250"
                dir="ltr"
              />
            </InputField>
            <InputField label={t('order.form.height')}>
              <input
                type="number"
                name="height"
                value={form.height}
                onChange={handleChange}
                min="0"
                step="0.01"
                className={inputCls}
                placeholder="240"
                dir="ltr"
              />
            </InputField>
          </div>
          <div className="mt-5">
            <InputField label={t('order.form.notes')}>
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                rows={3}
                className={`${inputCls} resize-none`}
                placeholder={t('order.form.notes')}
              />
            </InputField>
          </div>
        </div>

        {/* Plan Upload */}
        <div className="border-t border-charcoal/8 pt-7">
          <p className="label-text mb-4">
            <Upload size={12} className="inline me-2" />
            {t('order.form.planFile')}
          </p>
          <label
            htmlFor="plan-file"
            className={`flex flex-col items-center justify-center border-2 border-dashed p-8 cursor-pointer transition-colors bg-white/40 ${
              planFile ? 'border-gold/50 bg-gold/5' : 'border-charcoal/15 hover:border-gold/40'
            }`}
          >
            <input
              type="file"
              id="plan-file"
              accept="image/*,.pdf"
              onChange={(e) => setPlanFile(e.target.files[0] || null)}
              className="hidden"
            />
            <Upload size={28} className={`mb-3 ${planFile ? 'text-gold' : 'text-charcoal/30'}`} />
            {planFile ? (
              <div className="text-center">
                <p className="text-gold font-semibold text-sm">{planFile.name}</p>
                <p className="text-charcoal/40 text-xs mt-1">
                  {(planFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            ) : (
              <p className="text-charcoal/40 text-sm">{t('order.form.planFileHint')}</p>
            )}
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading || uploadLoading}
          className="w-full bg-gold text-charcoal-deep font-bold py-4 flex items-center justify-center gap-3 hover:opacity-90 transition-opacity disabled:opacity-50 text-base"
        >
          {loading || uploadLoading ? (
            <>
              <span className="w-5 h-5 border-2 border-charcoal border-t-transparent rounded-full animate-spin" />
              {uploadLoading ? t('order.form.uploading') : t('order.form.saving')}
            </>
          ) : (
            <>{t('order.form.submit')} <ArrowRight size={20} /></>
          )}
        </button>
      </form>
    </OrderLayout>
  );
};

export default StartPage;
